import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { useSessionStore } from '@/stores/session';
import { Menu, type MenuItem } from '@/components/Menu';
import { cx } from '@/lib/format';

export function WorkspaceSwitcher({ collapsed }: { collapsed: boolean }) {
  const me = useSessionStore((s) => s.me);
  const ws = useSessionStore((s) => s.workspace);
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  if (!ws) return null;
  const items: MenuItem[] = [
    { kind: 'label', label: 'Workspaces' },
    ...(me?.memberships ?? []).map<MenuItem>((m) => ({
      label: m.workspace.name,
      icon: m.workspace.id === ws.id ? <Check size={14} /> : <span className="inline-block h-3.5 w-3.5" />,
      onSelect: () => navigate(`/w/${m.workspace.slug}`),
    })),
    { kind: 'separator' },
    { label: 'Create workspace', icon: <Plus size={14} />, onSelect: () => navigate('/onboarding') },
  ];
  const initials = ws.name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <>
      <button
        ref={btn}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Workspace: ${ws.name}`}
        onClick={() => setOpen((o) => !o)}
        className={cx('flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-bg-sunken', collapsed && 'justify-center px-0')}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent text-xs font-semibold text-accent-fg">{initials}</span>
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{ws.name}</span>
            <ChevronsUpDown size={14} className="text-fg-subtle" />
          </>
        )}
      </button>
      {open && <Menu anchor={btn.current} items={items} onClose={() => setOpen(false)} />}
    </>
  );
}
